import * as React from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { useState, useEffect, useRef } from "react";
import Download from "../../assets/download-icon.png";
import Printer from "../../assets/printer-icon.png";
import { toast } from "react-toastify";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import { PageTabItemTitle } from "components/tabs/TabItem/PageTabItemTitle";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { process } from "@progress/kendo-data-query";
import {
    AmountFormatCell,
    DateFormatCell,
    AmountFormat,
    formatDateForAPI,
} from "components/common/formatter";
import { Label } from "@progress/kendo-react-labels";
import { Button } from "@progress/kendo-react-buttons";
import { getDocument } from "api";
import { getPostingDetails } from "services/postingsService";
import { reversePosting, addPostingDocuments, updatePostingDocuments, deleteSaleAllocation } from "api/accountsAPI";
import { useUploadForm } from "components/fileUploadHook";
import { onFileUpload } from "services/fileDataService";
import LoadingScreen from "components/common/Loading";
import { Tile } from "components/UIComponents/Tile";
import documentIcon from "../../assets/fileIcons/Document.svg";
import DocumentViewer from "components/common/DocumentViewer";
import EditPosting, { isPostingTypeEditable } from "./EditPosting";
import { formatDate } from "@progress/kendo-intl";
import DataList from "components/MatterDetails/DataList";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import { TextArea } from "@progress/kendo-react-inputs";
import {
    initializeTransactionPeriods,
    useTransactionPeriods,
    getCurrentPeriod,
    getSessionPeriodRecordFromStorage,
} from "services/transactionPeriodService";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { LuUpload } from "react-icons/lu";
import { RxCross2 } from "react-icons/rx";
import styles from "./Posting.module.scss";
import SubmitButton from "components/common/SubmitButton";
import ConfirmationDialog from "components/common/ConfirmationDialog";
import { useAccessRight, AccountsAccessRights } from "../../components/UserAccessRights.ts";
import BillWriteOffDialog, {
    isBillPosting,
    getBillWriteOffEligibility,
} from "./BillWriteOffDialog";
import { canActionPostingFromLedger } from "CONSTANT/Matter/PostingTypes";
import DisputePostingDialog, {
    isDisputablePostingType,
    getDisputeInfo,
} from "./DisputePostingDialog";

const ViewPosting = (props) => {
    const [mainData, setMainData] = useState();
    const [postingInfo, setPostingInfo] = useState({});
    const [doubleEntries, setDoubleEntries] = useState([]);
    const [postingName, setPostingName] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [showDocument, setShowDocument] = useState(false);
    const [documentViewerData, setDocumentViewerData] = useState([]);
    const [showEditPosting, setShowEditPosting] = useState(false);
    const [showReversePosting, setShowReversePosting] = useState(false);
    const [showWriteOffBill, setShowWriteOffBill] = useState(false);
    const [showDisputePosting, setShowDisputePosting] = useState(false);
    const [reason, setReason] = useState("");
    const [reversalDate, setReversalDate] = useState(new Date());
    const [allocations, setAllocations] = useState([]);
    const [billItems, setBillItems] = useState([]);
    const [selected, setSelected] = useState(0);

    const hasBillItems = billItems?.length > 0;
    const isBill = isBillPosting(postingInfo?.postingType);
    const writeOffEligibility = getBillWriteOffEligibility(postingInfo?.date);
    const isWriteOffDisabled =
        mainData?.isReversed === true || !writeOffEligibility.allowed;
    const writeOffDisabledReason = mainData?.isReversed
        ? "Posting is already reversed"
        : writeOffEligibility.allowed
            ? ""
            : writeOffEligibility.message;

    const isAmendedAmount = (original, current) =>
        Number(original ?? 0) !== 0 && Number(original) !== Number(current);

    const actionBtn = canActionPostingFromLedger(
        props.openingFrom,
        postingInfo?.postingType
    );

    const isEditable = isPostingTypeEditable(postingInfo?.postingType);
    const editDisabledReason = isEditable
        ? ""
        : `${postingInfo?.postingType || "This posting type"} cannot be edited`;

    const fileInputRef = useRef(null);
    const [isUploadingDoc, setIsUploadingDoc] = useState(false);
    const [documentRemoveTarget, setDocumentRemoveTarget] = useState(null);
    const [isRemovingDocument, setIsRemovingDocument] = useState(false);
    const [allocationToRemove, setAllocationToRemove] = useState(null);
    const [isRemovingAllocation, setIsRemovingAllocation] = useState(false);
    const { uploadForm } = useUploadForm();

    const { data: transactionsData } = useTransactionPeriods();
    const [sessionPeriod, setSessionPeriod] = useState(null);

    const canEditPosting = useAccessRight(AccountsAccessRights.EditPosting);

    const disputeInfo = getDisputeInfo(mainData);
    const canDispute =
        canEditPosting && isDisputablePostingType(postingInfo?.postingType);
    const isDisputeDisabled = mainData?.isReversed === true;
    const disputeDisabledReason = isDisputeDisabled
        ? "Posting is already reversed"
        : "";

    useEffect(() => {
        const stored = getSessionPeriodRecordFromStorage();
        if (stored) {
            setSessionPeriod(stored);
            return;
        }

        const init = async () => {
            const current = await getCurrentPeriod();
            if (current) {
                setSessionPeriod(current);
            }
        };
        init();
    }, []);

    useEffect(() => {
        fetchPostingDetails();
        initializeTransactionPeriods();
    }, []);

    const fetchPostingDetails = async () => {
        try {
            setIsLoading(true);
            const data = await getPostingDetails(props.transNo);
            setMainData(data);
            setAllocations(data?.allocations);
            setBillItems(data?.billItems || []);
            setDoubleEntries(data?.doubleEntries);
            setPostingName(data?.postingName);
            setPostingInfo(data?.postingInfo);
        } catch (error) {
            props.cancelEdit();
        } finally {
            setIsLoading(false);
        }
    };

    const renderAdditionalDetailsTile = () => {
        if (mainData?.additionalDetails && mainData.additionalDetails.length > 0) {
            return (
                <Tile className="py-3 px-lg-4 px-3 mt-3">
                    <div className="d-flex flex-column">
                        {mainData.additionalDetails.map((detail, index) => (
                            <span key={index} className="fs-md mb-1">
                                {detail}
                            </span>
                        ))}
                    </div>
                </Tile>
            );
        }
        return null;
    };

    const handleSubmit = async () => {
        try {
            const data = {
                reversalDate: formatDateForAPI(reversalDate),
                reverseReason: reason,
                reversalPeriod: sessionPeriod?.period,
            };
            const response = await reversePosting(props.transNo, data);
            if (response.status === 200) {
                toast.success("Posting reversed successfully");
                setShowReversePosting(false);
                props.cancelEdit();
                props.reloadData();
            }
        } catch (error) {
        }
    };

    const showDocumentFile = async (id) => {
        const { data } = await getDocument(id);
        setDocumentViewerData([data.data]);
        setShowDocument(true);
    };

    const handleDocumentFileChange = async (e) => {
        if (!e.target.files || !e.target.files[0]) return;
        try {
            setIsUploadingDoc(true);
            const fileData = onFileUpload(e);
            const parameters = {
                documentType: "Posting",
                matterId: postingInfo?.matterID,
            };
            const result = await uploadForm(fileData, e.target.files[0], parameters);
            const { fileID } = result.data.data;
            await addPostingDocuments(props.transNo, [{ documentFileId: fileID }]);
            toast.success("Document added successfully");
            await fetchPostingDetails();
            props.reloadData?.();
        } catch (error) {
            toast.error("Failed to upload document");
        } finally {
            setIsUploadingDoc(false);
            e.target.value = null;
        }
    };

    const removeDocumentById = async (documentId) => {
        try {
            await updatePostingDocuments(props.transNo, [{ documentId, active: false }]);
            toast.success("Document removed successfully");
            await fetchPostingDetails();
            props.reloadData?.();
            return true;
        } catch (error) {
            toast.error("Failed to remove document");
            return false;
        }
    };

    const openRemoveDocumentConfirm = (e, documentId, documentIndex) => {
        e.stopPropagation();
        setDocumentRemoveTarget({ documentId, documentIndex });
    };

    const handleConfirmRemoveDocument = async () => {
        if (!documentRemoveTarget) return;
        setIsRemovingDocument(true);
        const ok = await removeDocumentById(documentRemoveTarget.documentId);
        setIsRemovingDocument(false);
        if (ok) {
            setDocumentRemoveTarget(null);
        }
    };

    const closeRemoveDocumentConfirm = () => {
        if (!isRemovingDocument) {
            setDocumentRemoveTarget(null);
        }
    };

    const renderDataList = (
        text,
        value,
        keyStyles = {},
        valueClass = "mb-1",
        className
    ) => (
        <DataList
            text={text}
            value={value}
            keyClass="mb-1"
            valueClass={valueClass}
            width="w-100"
            className={className}
            keyStyles={{ minWidth: "100px", ...keyStyles }}
        />
    );

    const renderTile = () => (
        <Tile
            className="mt-3 py-1 px-lg-4 px-3 me-2 w-75 pb-1"
            heading="Posting Details"
        >
            <div>
                {renderDataList(
                    "Matter:",
                    mainData?.matterRef || "",
                    { minWidth: "100px" },
                    "mb-2"
                )}
            </div>
            <div className="d-flex justify-content-between me-1">
                <div>
                    {renderDataList(
                        "Date:",
                        formatDate(new Date(postingInfo?.date), "d", "en-GB")
                    )}
                </div>
                <div>
                    {renderDataList(
                        "Net Amount:",
                        AmountFormat(postingInfo?.netAmount),
                        {
                            minWidth: "115px",
                        },
                        "text-end"
                    )}
                </div>
            </div>
            <div className="d-flex justify-content-between me-1">
                <div>{renderDataList("Reference:", postingInfo?.reference)}</div>
                <div>
                    {renderDataList(
                        "VAT Amount:",
                        AmountFormat(postingInfo?.vatAmount),
                        {
                            minWidth: "115px",
                        },
                        "text-end"
                    )}
                </div>
            </div>
            <div className="d-flex justify-content-between me-1">
                <div>{renderDataList("Fee Earner:", postingInfo?.feeEarnerRef)}</div>
                <div>
                    {renderDataList(
                        "VAT Code:",
                        `${postingInfo.vatCode} - ${postingInfo.vatRate} %`,
                        { minWidth: "115px" },
                        "text-end"
                    )}
                </div>
            </div>
            <div className="d-flex justify-content-end me-1">
                <div>
                    {renderDataList(
                        "Gross Amount:",
                        AmountFormat(postingInfo.netAmount + postingInfo.vatAmount),
                        { minWidth: "115px" },
                        "text-end"
                    )}
                </div>
            </div>
            <div className="d-flex me-1">
                <div className="w-100">
                    {renderDataList("Details:", postingInfo.details)}
                </div>
            </div>
        </Tile>
    );

    const renderTransactionsTile = () => (
        <Tile className="py-1 px-lg-4 px-3 mt-3" heading="Transactions">
            <div className="d-flex justify-content-center align-items-start flex-column mt-1">
                <div className="my-1">
                    {renderDataList(
                        "Transaction Period:",
                        postingInfo.transactionPeriod,
                        {
                            minWidth: "145px",
                        }
                    )}
                </div>
                <div className="my-1">
                    {renderDataList("Transaction No:", props.transNo, {
                        minWidth: "145px",
                    })}
                </div>
                <div className="my-1">
                    {renderDataList("Added By:", mainData?.addedBy, {
                        minWidth: "145px",
                    })}
                </div>
                <div className="my-1">
                    {renderDataList(
                        "Date Added:",
                        formatDate(new Date(mainData?.dateAdded), "d", "en-GB") || "",
                        { minWidth: "145px" }
                    )}
                </div>
                <div className="d-flex align-items-start justify-content-between gap-2 mt-2 w-100">
                    {mainData?.documents?.length > 0 && (
                        <div>
                            {mainData.documents.map((doc, index) => (
                                <div
                                    key={index}
                                    className="d-flex align-items-center mb-1 cursor-pointer w-100 "
                                    onClick={() => showDocumentFile(doc)}
                                >
                                    <img src={documentIcon} alt="Document" width={18} height={20} />
                                    <p
                                        style={{
                                            color: "#1D3F6E",
                                            textDecoration: "underline",
                                            backgroundColor: "#F8F8F8"
                                        }}
                                        className="fs-md mb-0 ms-2 me-2"
                                    >
                                        Document {index + 1}
                                    </p>
                                    <span
                                        role="button"
                                        title="Remove document"
                                        onClick={(e) => openRemoveDocumentConfirm(e, doc, index + 1)}
                                        className="ms-auto d-flex align-items-center"
                                    >
                                        <RxCross2 size={14} color="red" />
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                    {isUploadingDoc && <LoadingScreen />}
                    <SubmitButton
                        className={`border lh-1  ${styles["addDocbtn"]}`}
                        onClick={() => !isUploadingDoc && fileInputRef.current?.click()}
                        disabled={isUploadingDoc}
                    >
                        Add <br /> Documents
                        <span className="mx-1" style={{ width: "1px", height: "20px", backgroundColor: "#707070", display: "inline-block" }} />
                        <span className="p-1 rounded" style={{ backgroundColor: "#707070" }}>
                            <LuUpload size={13} color="white" style={{ marginBottom: "1.5px" }} />
                        </span>
                    </SubmitButton>

                    <input
                        ref={fileInputRef}
                        type="file"
                        style={{ display: "none" }}
                        onChange={handleDocumentFileChange}
                    />
                </div>
                {showDocument && (
                    <DocumentViewer
                        onClose={() => setShowDocument(false)}
                        data={documentViewerData}
                        hideToolbar={true}
                    />
                )}
            </div>
        </Tile>
    );
    const handleSelect = (e) => {
        setSelected(e.selected);
    };

    const removeAllocation = (dataItem) => {
        setAllocationToRemove(dataItem);
    };

    const confirmRemoveAllocation = async () => {
        if (!allocationToRemove?.allocationId) {
            setAllocationToRemove(null);
            return;
        }
        setIsRemovingAllocation(true);
        try {
            await deleteSaleAllocation(props.transNo, allocationToRemove.allocationId);
            toast.success("Allocation removed successfully");
            setAllocationToRemove(null);
            await fetchPostingDetails();
        } catch (error) {
        } finally {
            setIsRemovingAllocation(false);
        }
    };

    const reverseAllocationCell = (cellProps) => {
        const allocationType = cellProps.dataItem.allocationType;
        if (allocationType !== "Sales") {
            return <td></td>;
        }

        if (postingInfo?.postingType === "BWO") {
            return <td></td>;
        }

        if (cellProps.dataItem?.postingType === "BWO") {
            return <td></td>;
        }


        console.log("cellProps.dataItem", cellProps.dataItem);

        return (
            <td className="text-center">
                <Button
                    className="btn m-0 p-0 border-0"
                    title="Remove Allocation"
                    onClick={(e) => {
                        e.stopPropagation();
                        removeAllocation(cellProps.dataItem);
                    }}
                >
                    <RxCross2 size={15} color="red" aria-hidden />
                </Button>
            </td>
        );
    };

    const originalAmountCell = (currentField) => (cellProps) => {
        const { dataItem, field } = cellProps;
        const original = dataItem[field];
        return (
            <td className="text-end">
                {isAmendedAmount(original, dataItem[currentField])
                    ? AmountFormat(original)
                    : ""}
            </td>
        );
    };

    const getBillItemGross = (item) =>
        Number(item.netAmount ?? 0) + Number(item.vatAmount ?? 0);

    const grossCell = ({ dataItem }) => (
        <td className="text-end">{AmountFormat(getBillItemGross(dataItem))}</td>
    );

    const sumBillItems = (items, field) =>
        items.reduce((total, item) => total + Number(item[field] ?? 0), 0);

    const amendedBillItemsTotal = (items, originalField, currentField) => {
        const amendedItems = items.filter((item) =>
            isAmendedAmount(item[originalField], item[currentField])
        );
        return amendedItems.length > 0
            ? sumBillItems(amendedItems, originalField)
            : null;
    };

    const groupFooterCell = (cellProps, value, align = "end") => (
        <td
            {...cellProps.tdProps}
            className={`${cellProps.tdProps?.className || ""} text-${align} fw-semibold`}
        >
            {value}
        </td>
    );

    const groupItems = (cellProps) => cellProps.dataItem?.items || [];

    const totalLabelGroupFooterCell = (cellProps) =>
        groupFooterCell(cellProps, "Total", "start");

    const sumGroupFooterCell = (field) => (cellProps) =>
        groupFooterCell(cellProps, AmountFormat(sumBillItems(groupItems(cellProps), field)));

    const amendedGroupFooterCell = (originalField, currentField) => (cellProps) => {
        const total = amendedBillItemsTotal(groupItems(cellProps), originalField, currentField);
        return groupFooterCell(cellProps, total === null ? "" : AmountFormat(total));
    };

    const grossGroupFooterCell = (cellProps) => {
        const total = groupItems(cellProps).reduce(
            (grossTotal, item) => grossTotal + getBillItemGross(item),
            0
        );
        return groupFooterCell(cellProps, AmountFormat(total));
    };

    const billItemsDataState = { group: [{ field: "billItemType" }] };

    const renderBillItemsGrid = () => (
        <div
            className="flex-grow-1 d-flex flex-column overflow-hidden mt-2"
            style={{ minHeight: 0 }}
        >
            <Grid
                data={process(billItems, billItemsDataState)}
                {...billItemsDataState}
                groupable={{ enabled: false, footer: "visible" }}
                style={{ height: "100%", minHeight: 0 }}
                className={`grid-fs13 grid-minwidth-col5-150 ${styles.billItemsGrid}`}
            >
                <GridColumn
                    field="billItemType"
                    title=" "
                    width="40px"
                    cells={{ data: () => <td /> }}
                />
                <GridColumn
                    field="date"
                    title="Date"
                    width="100px"
                    cells={{ data: DateFormatCell }}
                />
                <GridColumn field="nominalRef" title="Nominal" width="100px" />
                <GridColumn field="feeEarnerRef" title="FE" width="100px" />
                <GridColumn
                    field="details"
                    title="Details"
                    cells={{ groupFooter: totalLabelGroupFooterCell }}
                />
                <GridColumn
                    field="netAmount"
                    title="Net"
                    width="110px"
                    headerClassName="grid-header-center"
                    cells={{ data: AmountFormatCell, groupFooter: sumGroupFooterCell("netAmount") }}
                />
                <GridColumn
                    field="vatAmount"
                    title="VAT"
                    width="110px"
                    headerClassName="grid-header-center"
                    cells={{ data: AmountFormatCell, groupFooter: sumGroupFooterCell("vatAmount") }}
                />
                <GridColumn
                    field="originalNetAmount"
                    title="Unbilled Net"
                    headerClassName="grid-header-center"
                    width="110px"
                    cells={{
                        data: originalAmountCell("netAmount"),
                        groupFooter: amendedGroupFooterCell("originalNetAmount", "netAmount"),
                    }}
                />
                <GridColumn
                    field="originalVatAmount"
                    title="Unbilled Vat"
                    headerClassName="grid-header-center"
                    width="110px"
                    cells={{
                        data: originalAmountCell("vatAmount"),
                        groupFooter: amendedGroupFooterCell("originalVatAmount", "vatAmount"),
                    }}
                />
                <GridColumn
                    title="Gross"
                    headerClassName="grid-header-center"
                    width="110px"
                    cells={{ data: grossCell, groupFooter: grossGroupFooterCell }}
                />
            </Grid>
        </div>
    );

    const renderDoubleEntryTab = () => (
        <TabStrip
            keepTabsMounted={true}
            selected={selected}
            onSelect={handleSelect}
            className={`${styles.postingDetailsTabs} mt-2 pagetab w-100`}
        >
            {["Double Entry"].map((item, index) => (
                <TabStripTab key={index} title={<PageTabItemTitle title={item} />}>
                    {item === "Double Entry" && (
                        <div
                            className="flex-grow-1 d-flex flex-column overflow-hidden mt-2"
                            style={{ minHeight: 0 }}
                        >
                            <Grid
                                data={doubleEntries}
                                style={{ height: "100%", minHeight: 0 }}
                                className="grid-fs13"
                            >
                                <GridColumn
                                    field="date"
                                    title="Date"
                                    width="100px"
                                    cell={DateFormatCell}
                                />

                                <GridColumn field="nominalRef" title="Nominal" width="100px" />

                                <GridColumn field="details" title="Details" />

                                <GridColumn
                                    field="amount"
                                    title="Amount"
                                    cell={AmountFormatCell}
                                    width="120px"
                                    className="pe-3"
                                />

                                <GridColumn
                                    title="Reconciled"
                                    field="bankRecId"
                                    width="100px"
                                />
                            </Grid>
                        </div>
                    )}
                </TabStripTab>
            ))}

            {["Allocations"].map((item, index) => (
                <TabStripTab key={index} title={<PageTabItemTitle title={item} />}>
                    {item === "Allocations" && (
                        <div
                            className="flex-grow-1 d-flex flex-column overflow-hidden mt-2"
                            style={{ minHeight: 0 }}
                        >
                            <Grid
                                data={allocations}
                                style={{ height: "100%", minHeight: 0 }}
                                className="grid-fs13"
                            >
                                <GridColumn field="postingType" title="Type" width="120px" />
                                <GridColumn
                                    field="date"
                                    title="Date"
                                    width="120px"
                                    cell={DateFormatCell}
                                />
                                <GridColumn title="Reference" field="reference" width="150px" />
                                <GridColumn field="details" title="Details" />
                                <GridColumn
                                    field="amount"
                                    title="Amount"
                                    cell={AmountFormatCell}
                                    width="120px"
                                    headerClassName="grid-header-center"
                                />

                                <GridColumn
                                    title=""
                                    width="50px"
                                    cells={{ data: reverseAllocationCell }}
                                />

                            </Grid>
                        </div>
                    )}
                </TabStripTab>
            ))}

            {hasBillItems && (
                <TabStripTab title={<PageTabItemTitle title="Bill Items" />}>
                    {renderBillItemsGrid()}
                </TabStripTab>
            )}
        </TabStrip>
    );

    const renderReversePostingDialog = () => (
        <Dialog
            title="Reverse Posting"
            onClose={() => setShowReversePosting(false)}
            className="dialog-xxs"
        >
            <Tile className="py-3 px-4 mt-3 mx-4">
                <div className="w-100 mb-2">
                    <span className={styles["dialog-section-heading"]}>
                        {`${postingInfo.postingType} - ${postingName}`}
                    </span>
                </div>
                <div className="d-flex">
                    <div
                        style={{
                            width: "60%",
                        }}
                    >
                        {renderDataList(
                            "Date:",
                            formatDate(new Date(postingInfo?.date), "d", "en-GB")
                        )}
                    </div>
                    <div
                        className=""
                        style={{
                            width: "40%",
                        }}
                    >
                        {renderDataList("Net:", AmountFormat(postingInfo?.netAmount), {})}
                    </div>
                </div>
                <div className="d-flex">
                    <div
                        style={{
                            width: "60%",
                        }}
                    >
                        {renderDataList("Reference:", postingInfo?.reference)}
                    </div>
                    <div
                        style={{
                            width: "40%",
                        }}
                    >
                        {renderDataList("VAT:", AmountFormat(postingInfo?.vatAmount), {})}
                    </div>
                </div>

                <div className="d-flex">
                    <div
                        style={{
                            width: "60%",
                        }}
                    >
                        {renderDataList("Details:", postingInfo.details)}
                    </div>
                    <div
                        style={{
                            width: "40%",
                        }}
                    >
                        {renderDataList(
                            "Gross:",
                            AmountFormat(postingInfo.netAmount + postingInfo.vatAmount)
                        )}
                    </div>
                </div>
            </Tile>
            <Tile className="py-3 px-4 px-4 mt-3 mb-2 mx-4">
                <div className="d-flex flex-column">
                    <Label className="mb-1">Reversal Date</Label>
                    <DatePicker
                        className="date-picker px-1"
                        onChange={(e) => setReversalDate(e.target.value)}
                        width="265px"
                        value={reversalDate}
                    />
                </div>
                <div className="mt-3 d-flex flex-column">
                    <Label className="mb-1">Reversal Period</Label>
                    <ComboBox
                        data={transactionsData.filter((item) => !item.closed)}
                        textField="period"
                        filterable={true}
                        className="ps-2"
                        iconClassName="wa"
                        placeholder="Session Period"
                        style={{ width: "265px" }}
                        value={sessionPeriod}
                        onChange={(e) => {
                            if (e.value === null) return;
                            setSessionPeriod(e.value);
                        }}
                    />
                </div>
                <div className="mt-3 d-flex flex-column">
                    <Label className="mb-1">Reason</Label>
                    <TextArea
                        className="w-100"
                        placeholder="Write reason for reversal"
                        style={{ height: "75px", maxWidth: "100%" }}
                        onChange={(e) => setReason(e.target.value)}
                    />
                </div>
            </Tile>
            <DialogActionsBar>
                <div className="dialog-footer w-100">
                    <Button
                        className="common-btn bgBtnSecondary border-0 me-3"
                        onClick={() => setShowReversePosting(false)}
                    >
                        Close
                    </Button>
                    <Button className="common-btn border-0 me-5" onClick={handleSubmit}>
                        Reverse
                    </Button>
                </div>
            </DialogActionsBar>
        </Dialog>
    );

    return (
        <div>
            <Dialog
                title="Posting Details"
                onClose={props.cancelEdit}
                className="dialog-xl"
            >
                <div
                    className="d-flex flex-column h-100 overflow-hidden"
                    style={{ minHeight: 0 }}
                >
                    {isLoading ? (
                        <LoadingScreen />
                    ) : (
                        <div
                            className="flex-grow-1 d-flex flex-column overflow-auto px-lg-5 px-4"
                            style={{ minHeight: 0 }}
                        >
                            <div
                                className="fs-6 fw-bold d-flex align-items-center justify-content-between mt-3 border rounded px-4 py-1"
                                style={{
                                    backgroundColor: "#F8F8F8",
                                    borderColor: "#E6E6E6",
                                    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
                                }}
                            >
                                <div>
                                    <span>
                                        {postingInfo.postingType}{postingName ? ` - ${postingName}` : ""}
                                    </span>
                                    {disputeInfo.isDisputed && (
                                        <span className="ms-2" style={{ color: "#FF5555" }}>
                                            - Disputed
                                        </span>
                                    )}
                                </div>
                                <div className="d-flex">
                                    <div className="border px-2 py-1 rounded me-2">
                                        <img
                                            className="cursor-pointer"
                                            height={16}
                                            width={16}
                                            src={Printer}
                                            alt="Print"
                                        />
                                    </div>
                                    <div className="border px-2 py-1 rounded me-2">
                                        <img
                                            className="cursor-pointer"
                                            height={16}
                                            width={16}
                                            src={Download}
                                            alt="Download"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="d-flex mt-2">
                                {renderTile()}
                                {renderTransactionsTile()}
                            </div>
                            {renderAdditionalDetailsTile()}
                            <Tile
                                className="flex-grow-1 d-flex flex-column overflow-hidden py-3 px-4 mt-3 mb-3"
                                minHeight={280}
                            >
                                {renderDoubleEntryTab()}
                            </Tile>
                        </div>
                    )}
                </div>
                <DialogActionsBar>
                    <div className={`${actionBtn ? "justify-content-between" : "justify-content-end"} dialog-footer w-100`}>
                        {actionBtn && (
                            <div className="d-flex align-items-center">
                                <Tooltip
                                    anchorElement="target"
                                    position="right"
                                    parentTitle
                                >
                                    <span
                                        title={
                                            mainData?.isReversed
                                                ? `Posting is already reversed${mainData?.reverseInfo?.reversedBy
                                                    ? ` by ${mainData.reverseInfo.reversedBy}`
                                                    : ''}${mainData?.reverseInfo?.dateReversed
                                                        ? ` on ${new Date(mainData.reverseInfo.dateReversed).toLocaleString('en-GB', {
                                                            day: '2-digit',
                                                            month: '2-digit',
                                                            year: 'numeric',
                                                            hour: '2-digit',
                                                            minute: '2-digit',
                                                            hour12: true
                                                        })}`
                                                        : ''}`
                                                : ""
                                        }
                                    >
                                        {canEditPosting &&
                                            <Button
                                                className="common-btn border-0 ms-5"
                                                onClick={() => setShowReversePosting(true)}
                                                disabled={mainData?.isReversed === true}
                                            >
                                                Reverse Posting
                                            </Button>
                                        }
                                    </span>
                                </Tooltip>

                                {canDispute && (
                                    <Tooltip
                                        anchorElement="target"
                                        position="top"
                                        parentTitle
                                    >
                                        <span
                                            title={disputeDisabledReason}
                                            className="d-inline-block ms-3"
                                        >
                                            <Button
                                                className="common-btn border-0"
                                                onClick={() => setShowDisputePosting(true)}
                                                disabled={isDisputeDisabled}
                                                style={
                                                    isDisputeDisabled
                                                        ? { pointerEvents: "none" }
                                                        : undefined
                                                }
                                            >
                                                {disputeInfo.isDisputed ? "Undispute" : "Dispute"}
                                            </Button>
                                        </span>
                                    </Tooltip>
                                )}

                                {canEditPosting && isBill && (
                                    <Tooltip
                                        anchorElement="target"
                                        position="top"
                                        parentTitle
                                    >
                                        <span
                                            title={writeOffDisabledReason}
                                            className="d-inline-block ms-3"
                                        >
                                            <Button
                                                className="common-btn border-0"
                                                onClick={() => setShowWriteOffBill(true)}
                                                disabled={isWriteOffDisabled}
                                                style={
                                                    isWriteOffDisabled
                                                        ? { pointerEvents: "none" }
                                                        : undefined
                                                }
                                            >
                                                Write Off
                                            </Button>
                                        </span>
                                    </Tooltip>
                                )}
                            </div>
                        )}
                        <div className="d-flex align-items-center">
                            <Button
                                className="common-btn bgBtnSecondary border-0 me-3"
                                onClick={props.cancelEdit}
                            >
                                Close
                            </Button>
                            {canEditPosting && actionBtn && (
                                <Tooltip anchorElement="target" position="top" parentTitle>
                                    <span
                                        title={editDisabledReason}
                                        className="d-inline-block me-5"
                                    >
                                        <Button
                                            className="common-btn border-0"
                                            onClick={() => setShowEditPosting(true)}
                                            disabled={!isEditable}
                                            style={
                                                !isEditable ? { pointerEvents: "none" } : undefined
                                            }
                                        >
                                            Edit
                                        </Button>
                                    </span>
                                </Tooltip>
                            )}
                        </div>
                    </div>
                </DialogActionsBar>
            </Dialog>
            {showEditPosting && (
                <EditPosting
                    reloadPosting={props.reloadData}
                    cancelViewPosting={props.cancelEdit}
                    postingInfo={postingInfo}
                    doubleEntries={doubleEntries}
                    postingName={postingName}
                    transNo={props.transNo}
                    cancelEdit={() => setShowEditPosting(false)}
                />
            )}
            {showReversePosting && renderReversePostingDialog()}
            {showDisputePosting && (
                <DisputePostingDialog
                    transNo={props.transNo}
                    postingInfo={postingInfo}
                    postingName={postingName}
                    disputeInfo={disputeInfo}
                    onClose={() => setShowDisputePosting(false)}
                    onSaved={async () => {
                        setShowDisputePosting(false);
                        await fetchPostingDetails();
                        props.reloadData?.();
                    }}
                />
            )}
            {showWriteOffBill && (
                <BillWriteOffDialog
                    transNo={props.transNo}
                    postingId={mainData?.postingId}
                    postingInfo={postingInfo}
                    matterRef={mainData?.matterRef}
                    amountPaid={mainData?.amountPaid ?? postingInfo?.amountPaid}
                    allocations={allocations}
                    onClose={() => setShowWriteOffBill(false)}
                    onWriteOff={() => {
                        setShowWriteOffBill(false);
                        props.cancelEdit();
                        props.reloadData?.();
                    }}
                />
            )}
            {documentRemoveTarget && (
                <ConfirmationDialog
                    content={`Are you sure you want to remove Document ${documentRemoveTarget.documentIndex} from this posting?`}
                    onClickYes={handleConfirmRemoveDocument}
                    onClickNo={closeRemoveDocumentConfirm}
                    onClose={closeRemoveDocumentConfirm}
                    isLoading={isRemovingDocument}
                />
            )}
            {allocationToRemove && (
                <ConfirmationDialog
                    content="Are you sure you want to remove this allocation?"
                    onClickYes={confirmRemoveAllocation}
                    onClickNo={() => {
                        if (!isRemovingAllocation) setAllocationToRemove(null);
                    }}
                    onClose={() => {
                        if (!isRemovingAllocation) setAllocationToRemove(null);
                    }}
                    isLoading={isRemovingAllocation}
                />
            )}
        </div>
    );
};

export default ViewPosting;
